import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { LucideAngularModule, Edit, RefreshCw, Search, X } from 'lucide-angular';
import { LeadApplicationDto } from '../../../core/models/admin.models';
import { LeadApplicationsService } from '../../../core/services/lead-applications.service';

@Component({
  selector: 'app-admin-lead-applications',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    DialogModule,
    ButtonModule,
    TagModule,
    LucideAngularModule,
  ],
  templateUrl: './lead-applications.component.html',
})
export class LeadApplicationsComponent implements OnInit {
  private service = inject(LeadApplicationsService);
  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  applications = signal<LeadApplicationDto[]>([]);
  selected = signal<LeadApplicationDto | null>(null);
  total = signal(0);
  loading = signal(false);
  submitting = signal(false);
  dialogOpen = signal(false);
  error = signal('');

  page = signal(1);
  pageSize = 20;
  search = signal('');
  status = signal('');

  statuses = ['New', 'Contacted', 'Qualified', 'Converted', 'Rejected'];

  Search = Search;
  RefreshCw = RefreshCw;
  Edit = Edit;
  X = X;

  form = this.fb.group({
    fullName: ['', Validators.required],
    phoneNumber: ['', Validators.required],
    businessType: ['', Validators.required],
    companyName: [''],
    notes: [''],
    status: ['New', Validators.required],
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.service
      .getAll({
        page: this.page(),
        pageSize: this.pageSize,
        search: this.search() || undefined,
        status: this.status() || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.applications.set(res.data.items);
            this.total.set(res.data.total);
          }
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  onSearch(value: string): void {
    this.search.set(value);
    this.page.set(1);
    this.load();
  }

  setStatus(value: string): void {
    this.status.set(value);
    this.page.set(1);
    this.load();
  }

  openEdit(item: LeadApplicationDto): void {
    this.selected.set(item);
    this.error.set('');
    this.form.reset({
      fullName: item.fullName,
      phoneNumber: item.phoneNumber,
      businessType: item.businessType,
      companyName: item.companyName ?? '',
      notes: item.notes ?? '',
      status: item.status,
    });
    this.dialogOpen.set(true);
  }

  save(): void {
    if (this.form.invalid || !this.selected()) return;
    const value = this.form.value;
    this.submitting.set(true);
    this.service
      .update(this.selected()!.id, {
        fullName: value.fullName!,
        phoneNumber: value.phoneNumber!,
        businessType: value.businessType!,
        companyName: value.companyName || null,
        notes: value.notes || null,
        status: value.status!,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          if (res.success !== false) {
            this.dialogOpen.set(false);
            this.load();
          } else {
            this.error.set(res.message ?? res.errors?.join(' ') ?? 'Xato');
          }
        },
        error: (err) => {
          this.submitting.set(false);
          this.error.set(err?.error?.message ?? 'Xato');
        },
      });
  }

  formatDate(value: string): string {
    return new Date(value).toLocaleString('uz-UZ');
  }

  severity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'Converted') return 'success';
    if (status === 'Contacted' || status === 'Qualified') return 'info';
    if (status === 'Rejected') return 'danger';
    if (status === 'New') return 'warn';
    return 'secondary';
  }
}
